/**
 * ChoreographyDriver — the thin runtime for the Set-Piece Choreography system.
 * A sibling of TimeOfDayDriver / CombatMoodDriver: it watches the game state
 * (player position via camera, EventBus events, time-of-day phase, mapEnter),
 * feeds the pure ChoreographyModel, and executes the model's output (emit mood
 * events, apply fx patches via AtmosphereLayer.applyLayers).
 *
 * Composes with — never bypasses — the existing mood bus:
 *   timeline beat `set.sceneMood`  →  EVENTS.SCENE_MOOD  →  AtmosphereLayer
 *   timeline beat `set.combatMood` →  EVENTS.COMBAT_MOOD →  AtmosphereLayer
 *   timeline beat `fx`             →  AtmosphereLayer.applyLayers (live-tuner path)
 *   timeline beat `emit`           →  EventBus.emit (any event — boss phase, quest, …)
 *
 * So a cue's mood swell rides the SAME composition chain as the day/night clock
 * and combat heat — `final = base × diurnal × combat × choreography`. No bespoke
 * mood code per moment; the cue is pure data.
 *
 * Lifecycle: `start()` wires LEVEL_LOADED → read `scene.cues` from the resolved
 * scene → watch triggers → run timelines → LEVEL_UNLOADED clears state.
 * Idempotent + best-effort (never throws — a bad cue is skipped with a warning).
 */
import { EventBus, EVENTS, type EventName } from '../../../shared/EventBus';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { PixiOverlayHost } from '../../rendering/PixiOverlayHost';
import { gameLogger } from '../../../utils/GameLogger';
import { getMotionPrefs } from '../accessibility/motionPrefs';
import { resolveScene } from '../../effects/atmosphere/scene/resolveScene';
import { AtmosphereLayer } from '../../effects/atmosphere/AtmosphereLayer';
import { timeOfDayDriver } from './TimeOfDayDriver';
import {
  evaluateCues,
  cuesForEvent,
  cuesForMapEnter,
  createCueState,
  type CueState,
  type FiredBeat,
} from './ChoreographyModel';
import type { ChoreographyCue } from '../../../config/atmosphere/choreography.config';
import type { EffectLayer } from '../../../config/atmosphere/effectLayer.types';
import type { PlatformerLevel } from '../../../shared/CharacterTypes';

class ChoreographyDriverImpl {
  private started = false;
  private cues: ChoreographyCue[] = [];
  private states = new Map<string, CueState>();
  private unsubTick: (() => void) | null = null;
  private unsubLevel: (() => void) | null = null;
  private unsubUnload: (() => void) | null = null;
  private unsubEvent: Map<string, () => void> = new Map();
  /** Monotonic clock (ms) — starts at 0 on each level load. */
  private clockMs = 0;
  /** Pending mapEnter cues waiting for their delayMs. */
  private pendingMapEnter: { cueId: string; fireAtMs: number }[] = [];

  start(): void {
    if (this.started) return;
    this.started = true;
    try { (globalThis as { __choreography?: unknown }).__choreography = this; } catch { /* no global */ }
    const bus = EventBus.getInstance();
    this.unsubLevel = bus.on(EVENTS.LEVEL_LOADED, (data: { id?: string }) => {
      const levelId = data?.id;
      if (!levelId) { this.clearCues(); return; }
      this.loadCues(levelId);
    });
    this.unsubUnload = bus.on(EVENTS.LEVEL_UNLOADED, () => this.clearCues());
    this.unsubTick = PixiOverlayHost.addTickListener((dtMs: number) => this.tick(dtMs));
  }

  /** Load cues from the current level's scene block. */
  private loadCues(levelId: string): void {
    this.clearCues();
    try {
      const lvl = ServiceRegistry.getInstance().get('MapSystem', { optional: true })?.getCurrentLevel?.() as PlatformerLevel | undefined;
      if (!lvl) return;
      const scene = resolveScene(lvl);
      this.cues = scene.cues;
      if (this.cues.length === 0) return;

      // Wire event triggers — one EventBus listener per unique event name.
      const eventNames = new Set<string>();
      for (const cue of this.cues) {
        if (cue.trigger.on === 'event') eventNames.add(cue.trigger.event);
      }
      for (const eventName of eventNames) {
        const unsub = EventBus.getInstance().on(eventName as EventName, (payload: unknown) => {
          this.onEventFired(eventName, payload);
        });
        this.unsubEvent.set(eventName, unsub);
      }

      // Fire mapEnter cues (with their delayMs).
      this.clockMs = 0;
      const mapEnterCues = cuesForMapEnter(this.cues);
      for (const { cueId, delayMs } of mapEnterCues) {
        this.pendingMapEnter.push({ cueId, fireAtMs: delayMs });
      }

      gameLogger.info('vfx', `[Choreography] loaded ${this.cues.length} cues for ${levelId} (${eventNames.size} event triggers, ${mapEnterCues.length} mapEnter)`);
    } catch (e) {
      gameLogger.warn('vfx', `[Choreography] loadCues failed: ${(e as Error)?.message ?? e}`);
    }
  }

  /** Clear all cues + state (on level unload or reload). */
  private clearCues(): void {
    this.cues = [];
    this.states.clear();
    this.pendingMapEnter = [];
    this.clockMs = 0;
    for (const [, unsub] of this.unsubEvent) { try { unsub(); } catch { /* gone */ } }
    this.unsubEvent.clear();
  }

  /** An EventBus event fired — check if any cue matches + fire it. */
  private onEventFired(eventName: string, payload: unknown): void {
    const matchingIds = cuesForEvent(this.cues, eventName, payload);
    for (const cueId of matchingIds) {
      const state = this.getOrCreateState(cueId);
      if (state.fired) {
        // Check if the cue's trigger allows re-firing (once defaults to false
        // for event triggers — boss phases can repeat).
        const cue = this.cues.find((c) => c.id === cueId);
        if (cue?.trigger.on === 'event' && cue.trigger.once !== true) {
          // Re-arm: reset for a new timeline run.
          state.fired = true;
          state.firedAtMs = this.clockMs;
          state.emittedBeats = new Set();
        } else {
          continue; // already fired + once:true — skip
        }
      } else {
        state.fired = true;
        state.firedAtMs = this.clockMs;
        state.emittedBeats = new Set();
      }
    }
  }

  /** The per-tick evaluation — feed the model + execute its output. */
  private tick(dtMs: number): void {
    this.clockMs += dtMs;

    // Fire any pending mapEnter cues whose delay has elapsed.
    if (this.pendingMapEnter.length > 0) {
      const due = this.pendingMapEnter.filter((p) => this.clockMs >= p.fireAtMs);
      this.pendingMapEnter = this.pendingMapEnter.filter((p) => this.clockMs < p.fireAtMs);
      for (const { cueId } of due) {
        const state = this.getOrCreateState(cueId);
        state.fired = true;
        state.firedAtMs = this.clockMs;
        state.emittedBeats = new Set();
      }
    }

    if (this.cues.length === 0) return;

    // Get player position from the camera view (the camera follows the player).
    const view = PixiOverlayHost.getCameraView();
    const playerX = view?.centerX ?? 0;
    const playerY = view?.centerY ?? 0;

    // Get the current time-of-day phase.
    const timePhase = timeOfDayDriver.state().phase;

    const reduceMotion = getMotionPrefs().reduceMotion;

    const { actions } = evaluateCues(this.cues, this.states, {
      playerX,
      playerY,
      nowMs: this.clockMs,
      timePhase,
      reduceMotion,
    });

    for (const action of actions) {
      this.executeBeat(action);
    }
  }

  /** Execute a single fired beat — emit mood events, apply fx patches, emit events. */
  private executeBeat(action: FiredBeat): void {
    const beat = action.beat;
    const bus = EventBus.getInstance();

    // set: ease sceneMood / combatMood via the existing mood events.
    if (beat.set) {
      if (beat.set.sceneMood !== undefined) {
        // For eased transitions (overMs > 0), the driver emits the target value
        // now; the AtmosphereLayer's modulation ticker handles the visual ease
        // (the mood dial is a multiplier, not a tween). For 'instant' mode,
        // progress is 1 so this is the final value.
        bus.emit(EVENTS.SCENE_MOOD, { intensity: beat.set.sceneMood });
      }
      if (beat.set.combatMood !== undefined) {
        bus.emit(EVENTS.COMBAT_MOOD, { intensity: beat.set.combatMood });
      }
    }

    // fx: live-tweak a layer knob via the AtmosphereLayer's applyLayers path.
    if (beat.fx) {
      this.applyFxPatch(beat.fx);
    }

    // emit: fire an EventBus event.
    if (beat.emit) {
      bus.emit(beat.emit.event as EventName, beat.emit.payload);
    }
  }

  /** Apply an fx patch to a specific layer via the live-tuner path. */
  private applyFxPatch(fx: {
    layerId?: string;
    layerKind?: string;
    planeIndex?: number;
    patch: Record<string, unknown>;
  }): void {
    try {
      const layers = AtmosphereLayer.getActiveLayers();
      if (layers.length === 0) return;

      // Find the target layer by id (preferred) or kind + planeIndex.
      let targetIndex = -1;
      if (fx.layerId) {
        targetIndex = layers.findIndex((l) => (l as { id?: string }).id === fx.layerId);
      }
      if (targetIndex < 0 && fx.layerKind) {
        // Find by kind, optionally filtered by planeIndex (ordinal among same-kind).
        const sameKind = layers.map((l, i) => ({ l, i })).filter((x) => (x.l as { kind?: string }).kind === fx.layerKind);
        const idx = fx.planeIndex ?? 0;
        if (sameKind[idx]) targetIndex = sameKind[idx].i;
      }
      if (targetIndex < 0) {
        gameLogger.debug('vfx', `[Choreography] fx patch target not found (layerId=${fx.layerId}, kind=${fx.layerKind}, idx=${fx.planeIndex})`);
        return;
      }

      // Apply the patch as a partial override on the target layer.
      const patched = layers.map((l, i) => i === targetIndex ? { ...l, ...fx.patch } as EffectLayer : l);
      AtmosphereLayer.applyLayers(patched);
    } catch (e) {
      gameLogger.warn('vfx', `[Choreography] fx patch failed: ${(e as Error)?.message ?? e}`);
    }
  }

  private getOrCreateState(cueId: string): CueState {
    let state = this.states.get(cueId);
    if (!state) {
      state = createCueState(cueId);
      this.states.set(cueId, state);
    }
    return state;
  }

  /** Debug/QA — how many cues are loaded + how many have fired. */
  cueCount(): number { return this.cues.length; }
  firedCount(): number { return Array.from(this.states.values()).filter((s) => s.fired).length; }

  stop(): void {
    this.unsubTick?.();
    this.unsubTick = null;
    this.unsubLevel?.();
    this.unsubLevel = null;
    this.unsubUnload?.();
    this.unsubUnload = null;
    for (const [, unsub] of this.unsubEvent) { try { unsub(); } catch { /* gone */ } }
    this.unsubEvent.clear();
    this.clearCues();
    this.started = false;
  }
}

/** Process singleton — one choreography driver for the client. */
export const choreographyDriver = new ChoreographyDriverImpl();

/** Start the choreography driver (called once at game bootstrap). Idempotent. */
export function startChoreographyDriver(): void {
  choreographyDriver.start();
}
